import { fetchDeparturesJson, fetchRouteStops, getScheduledTime } from './sogralApi';
import type { SogralApiDeparture, SogralRouteStop } from '../types/sogral';

export interface LiveSearchDeparture {
  id: string;
  time: string;            // "HH:MM"
  date: string;            // "YYYY-MM-DD"
  destination: string;     // city the user searched for (may be intermediate)
  headOfLine: string;      // terminal city of this bus
  transporterName: string;
  seatsTotal: number;
  seatsSold: number;
  seatsAvailable: number;
  occupancyPct: number;    // 0-100
  priceDA: number | null;  // price for the searched stop (not necessarily terminal)
  status: 'ouvert' | 'assure' | 'annule';
  isPast: boolean;
  isIntermediateStop: boolean; // true = bus continues past searched destination
}

export function statusFromCode(code: number): LiveSearchDeparture['status'] {
  if (code === 2) return 'assure';
  if (code === 3) return 'annule';
  return 'ouvert';
}

export function parsePrice(raw: unknown): number | null {
  if (typeof raw === 'number' && raw > 0) return raw;
  if (typeof raw === 'string') {
    const n = Number(raw);
    if (!Number.isNaN(n) && n > 0) return n;
  }
  return null;
}

export function toLiveDeparture(d: SogralApiDeparture, refMinutes: number, todayStr: string): LiveSearchDeparture {
  const date = d.S8?.split('T')[0] ?? '';
  const time = getScheduledTime(d);
  const seatsTotal = d.P11 ?? 0;
  const seatsSold = typeof d.P12 === 'number' ? Math.max(0, d.P12) : 0;
  const seatsAvailable = Math.max(0, seatsTotal - seatsSold);
  const occupancyPct = seatsTotal > 0 ? Math.min(100, Math.round((seatsSold / seatsTotal) * 100)) : 0;

  let isPast = false;
  if (date === todayStr && time) {
    const [h, m] = time.split(':').map(Number);
    isPast = h * 60 + m < refMinutes;
  } else if (date < todayStr) {
    isPast = true;
  }

  return {
    id: d.P1,
    time,
    date,
    destination: d.S6 || '',
    headOfLine: d.S12 || '',
    transporterName: (d.P10 || '').trim(),
    seatsTotal,
    seatsSold,
    seatsAvailable,
    occupancyPct,
    priceDA: parsePrice(d.S15),
    status: statusFromCode(d.P8),
    isPast,
    isIntermediateStop: false,
  };
}

export interface LiveSearchParams {
  agencyId: number;        // 0 = both Alger stations, 1 or 2 = specific
  destinationCode: string; // P1 from SogralDestination, e.g. "213-000015001"
  date: string;            // "YYYY-MM-DD"
  fresh?: boolean;         // true = contourne le cache (mode live)
}

// Extract the wilaya number from an Algerian P1 code "213-000015001" → 15.
// Only Algerian codes (prefix "213-") embed the wilaya in the suffix.
// Non-Algerian codes (e.g. "216-000002000" = TUNIS) return null → exact-match only.
export function wilayaFromP1(p1: string): number | null {
  if (!p1.startsWith('213-')) return null;
  const suffix = p1.split('-')[1];
  if (!suffix) return null;
  const n = parseInt(suffix, 10);
  if (Number.isNaN(n)) return null;
  return Math.floor(n / 1000);
}

export interface AllStationParams {
  agencyId: number;
  date: string;
  fresh?: boolean;
}

export async function fetchAllStationDepartures(params: AllStationParams): Promise<LiveSearchDeparture[]> {
  const now = new Date();
  const refMinutes = now.getHours() * 60 + now.getMinutes();
  const todayStr = now.toISOString().split('T')[0];

  const agencyIds = params.agencyId === 0 ? [1, 2] : [params.agencyId];
  const all: SogralApiDeparture[] = [];

  await Promise.all(
    agencyIds.map(async (id) => {
      try {
        const deps = await fetchDeparturesJson(id, params.fresh);
        all.push(...deps);
      } catch { /* skip failed agency */ }
    }),
  );

  return all
    .filter((d) => d.S8?.split('T')[0] === params.date)
    .map((d) => toLiveDeparture(d, refMinutes, todayStr))
    .sort((a, b) => a.time.localeCompare(b.time));
}

export async function searchLiveDepartures(params: LiveSearchParams): Promise<LiveSearchDeparture[]> {
  const wilaya = wilayaFromP1(params.destinationCode);

  const now = new Date();
  const refMinutes = now.getHours() * 60 + now.getMinutes();
  const todayStr = now.toISOString().split('T')[0];

  const agencyIds = params.agencyId === 0 ? [1, 2] : [params.agencyId];
  const all: SogralApiDeparture[] = [];
  for (const id of agencyIds) {
    try {
      const deps = await fetchDeparturesJson(id, params.fresh);
      all.push(...deps);
    } catch {
      // skip failed agency
    }
  }

  // Non-Algerian destinations (e.g. TUNIS, wilaya=null): wilaya filter doesn't apply,
  // use exact P4/S11 match only so we don't return wrong-wilaya Algerian buses.
  if (wilaya === null) {
    const exact = all
      .filter((d) => {
        if (d.S8?.split('T')[0] !== params.date) return false;
        return d.S11 === params.destinationCode || d.P4 === params.destinationCode;
      })
      .map((d) => toLiveDeparture(d, refMinutes, todayStr));
    return exact.sort((a, b) => a.time.localeCompare(b.time));
  }

  // Multi-field filter: wilaya match (S7) covers all communes in the target wilaya.
  // S11/P4 exact match catches routes where SOGRAL leaves S7 wrong/unset.
  const filtered = all.filter((d) => {
    if (d.S8?.split('T')[0] !== params.date) return false;
    if (Number(d.S7) === wilaya) return true;
    if (d.S11 === params.destinationCode) return true;
    if (d.P4 === params.destinationCode) return true;
    return false;
  });

  // Enrich same-wilaya results: buses whose terminal is NOT the searched city get
  // route stop lookup to find the correct stop-specific price.
  // Route stops are cached by P6, so unique routes are fetched only once.
  const enriched = await Promise.all(
    filtered.map(async (d): Promise<LiveSearchDeparture> => {
      const dep = toLiveDeparture(d, refMinutes, todayStr);

      const isExact = d.P4 === params.destinationCode || d.S11 === params.destinationCode;
      if (isExact) return dep;

      try {
        const stops = await fetchRouteStops(d.S1, d.P4, d.P6);
        const targetStop = stops.find((s) => s.P1 === params.destinationCode);
        if (targetStop) {
          dep.isIntermediateStop = true;
          dep.destination = targetStop.S5;
          if (targetStop.S6 != null && targetStop.S6 > 0) dep.priceDA = targetStop.S6;
        }
      } catch { /* show as-is */ }

      return dep;
    }),
  );

  // Cross-wilaya intermediate stops: buses going to a DIFFERENT wilaya that still
  // pass through the searched city (e.g. Alger→Ain Sefra stops at Relizane W48).
  // Only triggered when same-wilaya filter found nothing, to keep normal searches fast.
  // Route stops are cached by P6, so repeated searches are instant.
  if (enriched.length === 0) {
    const crossCandidates = all.filter((d) => {
      if (d.S8?.split('T')[0] !== params.date) return false;
      if (Number(d.S7) === wilaya) return false; // already handled
      if (d.P4 === params.destinationCode || d.S11 === params.destinationCode) return false;
      return true;
    });

    // One route-stop lookup per unique P6 (not per departure)
    const uniqueRoutes = new Map<number, SogralApiDeparture>();
    for (const d of crossCandidates) {
      if (d.P6 != null && !uniqueRoutes.has(d.P6)) uniqueRoutes.set(d.P6, d);
    }

    // Fetch all routes in parallel (cache means each P6 hits the network at most once ever;
    // subsequent cross-wilaya searches for any destination are instant after the first cold pass)
    const routeHits = new Map<number, SogralRouteStop>();
    await Promise.all(
      [...uniqueRoutes.entries()].map(async ([routeId, rep]) => {
        try {
          const stops = await fetchRouteStops(rep.S1, rep.P4, routeId);
          const hit = stops.find((s) => s.P1 === params.destinationCode);
          if (hit) routeHits.set(routeId, hit);
        } catch { /* skip */ }
      }),
    );

    const crossResults = crossCandidates
      .filter((d) => routeHits.has(d.P6))
      .map((d) => {
        const dep = toLiveDeparture(d, refMinutes, todayStr);
        const stop = routeHits.get(d.P6)!;
        dep.isIntermediateStop = true;
        dep.destination = stop.S5;
        if (stop.S6 != null && stop.S6 > 0) dep.priceDA = stop.S6;
        return dep;
      });

    enriched.push(...crossResults);
  }

  return enriched.sort((a, b) => a.time.localeCompare(b.time));
}
